// Polyfills; they must always be first!!!
import '../src/polyfills';
// styles
import 'styles/globals.css';

import { GaSignUpBridge } from '@components/common/GaSignUpBridge';
import { GaJobApplyBridge } from '@components/common/GaJobApplyBridge';

import { appWithTranslation, UserConfig } from 'next-i18next';
import { Provider } from 'react-redux';
import { store } from 'src/app';
import { useRouter } from 'next/compat/router';
import { useEffect, ReactNode } from 'react';
import { CookieConsent } from '@components/features/cookie-consent/CookieConsent';
import { Session } from 'next-auth';
import { AuthSessionProvider } from '@auth/context/AuthSessionProvider';
import { i18nConfig } from 'i18n-config';
import SymfonyRouter from '@services/symfony';
import { LayoutDefault } from '@components/layout';
import { ToastsContainer, ErrorBoundary, ClientSideErrorPage } from '@components/common';
import { AppProps } from 'next/app';
import { NextPage } from 'next';
import Head from 'next/head';
import NiceModal from '@ebay/nice-modal-react';
import { EventsTrackingProvider } from '@components/contexts/EventsTrackingProvider';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { HeaderSearchContextProvider } from '@components/contexts/HeaderSearchContext';
import { useVoiceAgentModalAutoOpen } from '@hooks/common/useVoiceAgentModalAutoOpen';

// Canonical url functions
import { getCanonicalUrl, getSwitcherPaths, getLocaleFromI18nPath } from '@services/i18nPaths';
import { logErrorEvent } from '@utils/logger/logger';
import { fontLocal } from '@utils/fonts';
import { Ga4UserLinker } from '@components/analytics/Ga4UserLinker';
import nextI18nextConfig from '../next-i18next.config';

// Next.js Local font

type GetLayout = (page: ReactNode) => ReactNode;

const VoiceAgentAutoOpen = () => {
  useVoiceAgentModalAutoOpen();
  return null;
};

type Page<P = Record<string, never>, IP = P> = NextPage<P, IP> & {
  getLayout?: GetLayout;
};

type Props<P = Record<string, never>> = AppProps<P> & {
  Component: Page<P>;
  pageProps: P & {
    session?: Session | null;
  };
};

const defaultGetLayout: GetLayout = (page: ReactNode): ReactNode => <LayoutDefault>{page}</LayoutDefault>;

const MyApp = ({ Component, pageProps }: Props) => {
  const router = useRouter();
  const routerQueryLocale = Array.isArray(router?.query?.locale) ? router?.query?.locale[0] : router?.query?.locale;
  const asPath = router?.asPath ?? '/';
  // For i18n paths (like /locuri-de-munca), get locale from the path mapping
  // Otherwise, use the locale from the query (router params)
  const rawLocale = getLocaleFromI18nPath(asPath) ?? routerQueryLocale;
  const locale = rawLocale && i18nConfig.locales.includes(rawLocale as Locale) ? rawLocale : i18nConfig.defaultLocale;
  const { session, ...restPageProps } = pageProps;

  SymfonyRouter.setLocale(locale);

  useEffect(() => {
    // listen to errors
    window.addEventListener('error', logErrorEvent);

    return () => {
      // remove error listener
      window.removeEventListener('error', logErrorEvent);
    };
  }, []);

  const getLayout = Component.getLayout || defaultGetLayout;

  // Get the canonical URL (string) and Meta alternates (array)
  const canonicalUrl = getCanonicalUrl(asPath);
  const metaAlternates = getSwitcherPaths(asPath);

  return (
    <>
      {/* font as CSS variable, to be passed to tailwind.config.js */}
      <style jsx global>
        {`
          :root {
            --font-base: ${fontLocal.style.fontFamily};
          }
        `}
      </style>

      <ErrorBoundary fallbackComponent={<ClientSideErrorPage />}>
        <Provider store={store}>
          <AuthSessionProvider session={session}>
            <EventsTrackingProvider>
              <NiceModal.Provider>
                <VoiceAgentAutoOpen />
                <CookieConsent />
                <Ga4UserLinker />
                <GaSignUpBridge />
                <GaJobApplyBridge />

                {/*
            Default head; missing meta tags in custom Headers will be inherited. IE: if we put only
            title & description in our custom Head for a page, the canonical meta will be inherited.
          */}
                <Head>
                  <title>BestJobs</title>
                  <meta name="description" content="BestJobs" />
                  <link rel="canonical" href={canonicalUrl} key={`canonical-${locale}`} />
                  <meta name="apple-itunes-app" content="app-id=584324978" />
                  {
                    /* Alternate languages urls for page */
                    metaAlternates
                      .filter((alternate) => alternate[0] !== locale)
                      .map((lang) => {
                        // made the variables for readability
                        const langName = lang[0];
                        const urlPath = lang[1];

                        return <link rel="alternate" hrefLang={langName} href={getCanonicalUrl(urlPath)} key={`alternate-${langName}`} />;
                      })
                  }
                </Head>

                {/* Toasts container */}
                <ToastsContainer />

                <HeaderSearchContextProvider>
                  <FilterDrawerProvider>{getLayout(<Component {...restPageProps} />)}</FilterDrawerProvider>
                </HeaderSearchContextProvider>
              </NiceModal.Provider>
            </EventsTrackingProvider>
          </AuthSessionProvider>
        </Provider>
      </ErrorBoundary>
    </>
  );
};

export default appWithTranslation(MyApp, nextI18nextConfig as UserConfig);
